<template>
  <div class="bg-white">
    <!-- Start header with image -->
    <div class="container-fluid px-0">
      <div class="lpg-header position-relative d-none d-md-block">
        <img :src="company.header_url" class="img-fluid vw-100" alt="..." />
      </div>
    </div>
    <!-- End header with image -->
    <div class="container-fluid px-0 mb-5">
      <div class="position-relative lpg-profile-cv">
        <div class="row g-0">
          <!-- left side -->
          <div class="col-12 col-md-4 col-lg-4 col-xl-3 px-4 pb-4 lpg-bg-primary ">
            <div class="d-flex justify-content-center align-items-center lpg-up-profile-pic">
              <div class="d-flex flex-column">
                <div class="mt-5 text-center d-block mx-auto">
                  <Link :href="$route('company.stand', company.id)" target="_blank">
                    <div class="profile-pic">
                      <img :src="company.logo_url" />
                    </div>
                  </Link>
                </div>
                <div class="my-2 text-center">
                  <Link
                    :href="$route('company.stand', company.id)"
                    class="text-decoration-none text-reset"
                  >
                    <h2 class="lpg-form-title fs-4">{{ company.name }}</h2>
                  </Link>
                </div>
                <div class="mb-4 mx-3 mx-md-1 text-start">
                  <SidebarLinkItem
                    v-for="item in sidebar_filtered_items"
                    :key="item.key"
                    class="my-2"
                    :label="company[item.key]"
                    :icon="'bi bi-' + item.icon"
                    :url="company[item.key]"
                  />
                </div>
              </div>
            </div>

            <div class="lpg-form lpg-form-box px-0">
              <section>
                <div class="lpg-profile-content row row-cols-2 row-cols-md-1 gy-2"></div>
              </section>
            </div>
          </div>

          <!-- right side -->
          <div class="col-12 col-md col-lg text-start bg-white">
            <div class="lpg-body-side-right">
              <div class="lpg-form lpg-form-box px-0">
                <section class="my-5">
                  <div class="lpg-profile-content container-sm">
                    <page-title class="mb-2" :title="offer.title" :subtitle="offer.category" />
                    <div>
                      <action-button
                        :class="[
                          'btn btn-default d-block d-md-inline-block mx-auto px-5 my-1',
                          { 'disabled bg-secondary': hasUserApplied },
                        ]"
                        :isLoading="isLoading"
                        style="letter-spacing: 0.15em;"
                        @click="clickOnApplyButton"
                      >
                        {{
                          isUserLoggedIn
                            ? hasUserApplied
                              ? 'Ya has aplicado'
                              : '¡Aplica ahora!'
                            : '¡Regístrate para aplicar!'
                        }}
                      </action-button>
                    </div>
                    <div class="my-3">
                      <h4 class="fs-5 fw-bold">
                        Salario:
                        {{
                          offer.salary_to_be_agreed
                            ? 'A convenir'
                            : `$${offer.salary.min} ${
                                offer.salary.max ? `- $${offer.salary.max}` : ''
                              }`
                        }}
                      </h4>
                    </div>
                    <div v-if="offer.is_remote" class="row my-2">
                      <div class="col">
                        <badge class="fs-6" bgColor="#578097" textColor="white"> Remoto: Si </badge>
                      </div>
                    </div>
                    <div class="row g-2 flex-wrap mb-5">
                      <div v-for="(item, key) in badges" :key="key" class="col-12 col-md-auto">
                        <badge class="fs-6"> {{ item.text }}: {{ offer[item.key] }} </badge>
                      </div>
                    </div>

                    <profile-section>
                      <template v-slot:title>Descripción</template>
                      <template v-slot:content
                        >{{
                          offer.full_description ? offer.full_description : offer.short_description
                        }}
                      </template>
                    </profile-section>

                    <div class="row">
                      <div
                        v-if="offer.job_responsibilities"
                        :class="['col-12', { 'col-lg-6': offer.required_qualifications }]"
                      >
                        <profile-section>
                          <template v-slot:title>Funciones</template>
                          <template v-slot:content>{{ offer.job_responsibilities }} </template>
                        </profile-section>
                      </div>
                      <div
                        v-if="offer.required_qualifications"
                        :class="['col-12', { 'col-lg-6': offer.job_responsibilities }]"
                      >
                        <profile-section>
                          <template v-slot:title>Requisitos</template>
                          <template v-slot:content>{{ offer.required_qualifications }} </template>
                        </profile-section>
                      </div>
                    </div>

                    <div v-if="offer.benefits_and_perks" class="col-12">
                      <profile-section>
                        <template v-slot:title>Prestaciones adicionales</template>
                        <template v-slot:content>{{ offer.benefits_and_perks }} </template>
                      </profile-section>
                    </div>
                    <br class="my-3" />
                    <div
                      v-if="Array.isArray(related_offers) && related_offers.length > 0"
                      class="mt-5 mb-3"
                    >
                      <page-title class="fs-2 mb-2" title="Ofertas relacionadas"></page-title>
                      <related-job-postings-section :items="related_offers" />
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '@/Layouts/Layout'
import { Link } from '@inertiajs/inertia-vue'
import PageTitle from '@/Components/PageTitle'
import ProfileSection from '@/Components/ProfileSection'
import SidebarLinkItem from '@/Components/SidebarLinkItem'
import Badge from '@/Components/Badge'
import ActionButton from '@/Components/ActionButton'
import RelatedJobPostingsSection from '@/Components/RelatedJobPostingsSection'

export default {
  name: 'JobPostingProfile',
  layout: Layout,
  components: {
    PageTitle,
    ProfileSection,
    SidebarLinkItem,
    Badge,
    ActionButton,
    RelatedJobPostingsSection,
    Link,
  },
  props: {
    errors: {
      type: Object,
    },
    company: {
      type: Object,
      required: true,
    },
    offer: {
      type: Object,
      required: true,
    },
    related_offers: {
      type: Array,
      required: false,
    },
  },
  data() {
    return {
      isLoading: false,
      badges: [
        { text: 'Cargo ofertado', key: 'offered_position' },
        { text: 'Cantidad de vacantes', key: 'vacancies' },
        { text: 'Tipo de contrato', key: 'job_contract_type' },
        { text: 'Escolaridad requerida', key: 'required_education_level' },
      ],
      sidebar_items: [
        {
          icon: 'globe',
          key: 'website_url',
        },
        {
          icon: 'facebook',
          key: 'fb_url',
        },
        {
          icon: 'instagram',
          key: 'ig_url',
        },
        {
          icon: 'linkedin',
          key: 'linkedin_url',
        },
      ],
    }
  },
  computed: {
    sidebar_filtered_items() {
      return this.sidebar_items.filter(item => this.company[item.key] !== null)
    },
    isUserLoggedIn() {
      return this.$page.props.auth.user !== null
    },
    hasUserApplied() {
      return this.isUserLoggedIn && this.offer.has_user_applied
    },
  },
  mounted() {
    this.$nextTick(function() {
      window.scrollTo(0, 0)
    })
  },
  methods: {
    clickOnApplyButton() {
      if (this.isUserLoggedIn) {
        this.$inertia.post(
          this.$route('application.create'),
          { job_posting_id: this.offer.id },
          {
            onBefore: () => {
              this.isLoading = true
            },
            onFinish: () => {
              this.isLoading = false
            },
            onSuccess: page => {
              if (Object.keys(this.errors).length <= 0)
                this.$swal.fire({
                  title: page.props.flash.message
                    ? page.props.flash.message
                    : '🎉 Has aplicado con éxito a la plaza',
                  icon: 'success',
                })
            },
            onError: errors => {
              this.$swal.fire({
                title: 'Ha ocurrido un error',
                icon: 'error',
                html:
                  '<ul class="text-start fs-6">' +
                  Object.values(this.errors).reduce(
                    (prev, curr) => (prev += `<li>${curr}</li>`),
                    '',
                  ) +
                  '</ul>',
              })
            },
          },
        )
      } else {
        this.$inertia.get(this.$route('register'), {}, { preserveScroll: false })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.text-dark {
  color: #444444 !important;
}
</style>
